import { useEffect, useRef } from 'react';

// Direct wrapper around window.Telegram.WebApp (loaded from telegram-web-app.js
// in public/index.html). Safe to call outside Telegram — all methods no-op
// and popup/alert fall back to window.alert.

const getWebApp = () => {
    if (typeof window === 'undefined') return null;
    return window.Telegram?.WebApp ?? null;
};

export const tg = {
    get webApp() { return getWebApp(); },

    haptic(style = 'light') {
        const hf = getWebApp()?.HapticFeedback;
        if (!hf) return;
        try { hf.impactOccurred(style); } catch (e) {}
    },

    hapticNotify(type = 'success') {
        const hf = getWebApp()?.HapticFeedback;
        if (!hf) return;
        try { hf.notificationOccurred(type); } catch (e) {}
    },

    hapticSelection() {
        const hf = getWebApp()?.HapticFeedback;
        if (!hf) return;
        try { hf.selectionChanged(); } catch (e) {}
    },

    showAlert(message) {
        const wa = getWebApp();
        if (wa?.showAlert) {
            try { wa.showAlert(String(message)); return; } catch (e) {}
        }
        if (typeof window !== 'undefined') window.alert(String(message));
    },

    showPopup({ title, message, buttons }) {
        const wa = getWebApp();
        if (wa?.showPopup) {
            try {
                wa.showPopup({ title, message, buttons: buttons ?? [{ type: 'ok' }] });
                return;
            } catch (e) {}
        }
        if (typeof window !== 'undefined') window.alert(String(message));
    },
};

/**
 * Mount Telegram BackButton for the current page. Auto-hides on unmount.
 * The onClick handler is stored in a ref so passing a fresh closure each
 * render doesn't rebind the Telegram event (which would flicker).
 */
export function useBackButton(onClick, { enabled = true } = {}) {
    const handlerRef = useRef(onClick);
    handlerRef.current = onClick;

    useEffect(() => {
        const bb = getWebApp()?.BackButton;
        if (!bb || !enabled) return;

        const handler = () => {
            try { tg.haptic('light'); } catch (e) {}
            handlerRef.current?.();
        };

        try { bb.onClick(handler); bb.show(); } catch (e) {}

        return () => {
            try { bb.offClick(handler); bb.hide(); } catch (e) {}
        };
    }, [enabled]);
}

/**
 * Mount Telegram MainButton with given text/progress/show flags. Like
 * useBackButton, the onClick handler is ref-tracked so re-renders don't
 * thrash the Telegram API.
 */
export function useMainButton({ text, onClick, show = true, progress = false, enabled = true }) {
    const handlerRef = useRef(onClick);
    handlerRef.current = onClick;

    useEffect(() => {
        const mb = getWebApp()?.MainButton;
        if (!mb) return;

        const handler = () => {
            try { tg.haptic('medium'); } catch (e) {}
            handlerRef.current?.();
        };

        try { mb.onClick(handler); } catch (e) {}

        return () => {
            try { mb.offClick(handler); mb.hide(); } catch (e) {}
        };
    }, []);

    useEffect(() => {
        const mb = getWebApp()?.MainButton;
        if (!mb) return;
        try {
            if (text) mb.setText(text);
            if (enabled) mb.enable?.(); else mb.disable?.();
            if (progress) mb.showProgress?.(false); else mb.hideProgress?.();
            if (show) mb.show(); else mb.hide();
        } catch (e) {}
    }, [text, show, progress, enabled]);
}

/**
 * Apply Telegram theme params to CSS custom properties on :root so the app
 * inherits the user's light/dark theme. Re-applies on themeChanged event.
 */
export function applyTelegramTheme() {
    const wa = getWebApp();
    if (!wa) return;

    const apply = () => {
        const p = wa.themeParams ?? {};
        const root = document.documentElement;
        const isDark = wa.colorScheme === 'dark';
        const set = (name, value, fallback) => {
            root.style.setProperty(name, value || fallback);
        };

        if (isDark) {
            // Force a softer dark palette regardless of what Telegram sends.
            // Telegram's default dark theme hands us pure #ffffff text on a
            // near-black background — ~21:1 contrast — which reads as harsh.
            // Off-white text (#e6e6e6) + a slightly lifted bg is easier on
            // the eyes while still well past WCAG AAA (~13:1).
            set('--tg-bg-color', p.bg_color, '#17212b');
            set('--tg-secondary-bg-color', p.secondary_bg_color, '#232e3c');
            set('--tg-text-color', '#e6e6e6');
            set('--tg-hint-color', '#8a96a5');
            set('--tg-link-color', p.link_color, '#6ab3f3');
            set('--tg-button-color', p.button_color, '#fdce9a');
            set('--tg-button-text-color', p.button_text_color, '#17212b');
            set('--tg-section-separator-color', 'rgba(255, 255, 255, 0.08)');
            set('--tg-destructive-text-color', p.destructive_text_color, '#ff6b6b');
        } else {
            set('--tg-bg-color', p.bg_color, '#ffffff');
            set('--tg-secondary-bg-color', p.secondary_bg_color, '#f4f4f5');
            set('--tg-text-color', p.text_color, '#000000');
            set('--tg-hint-color', p.hint_color, '#585f6d');
            set('--tg-link-color', p.link_color, '#2481cc');
            set('--tg-button-color', p.button_color, '#fdce9a');
            set('--tg-button-text-color', p.button_text_color, '#000000');
            set('--tg-section-separator-color', p.section_separator_color, '#c7cbd3');
            set('--tg-destructive-text-color', p.destructive_text_color, '#ff3b30');
        }

        root.dataset.tgTheme = wa.colorScheme || 'light';
        // Marker for CSS to hide fallback inline CTAs — when MainButton is
        // available the real action surface is the Telegram bottom button.
        if (wa.MainButton) root.dataset.tgEmbedded = 'true';
    };

    apply();
    try { wa.onEvent('themeChanged', apply); } catch (e) {}
    try { wa.ready?.(); } catch (e) {}
    try { wa.expand?.(); } catch (e) {}
}
